/**
 * KAN-862: хуки канбан-доски CRM.
 * - useCrmPipeline: обзор доски (колонки со счётчиками и карточки);
 * - useCrmColumnCards: карточки колонки порциями по 50 (свёрнутая колонка,
 *   "Загрузить ещё"); без колонки запроса нет;
 * - useEditBoardCard: перенос карточки с изменением клиента (менеджер,
 *   теплота, этап), оптимистично в кеше текущего вида доски; после ответа обновляются
 *   доска, таблица CRM, блок внимания (всё под ATTENTION_QUERY_KEY) и кеши правки
 *   (clientEditKeys: карточка, клиенты, менеджеры / теплота; у этапа воронка).
 * KAN-865: attentionOnly - только клиенты, которые требуют внимания (?attention=1).
 * KAN-880: фильтры search / manager / stale (CrmLeadFilterParams) входят в ключ.
 * KAN-882: вид доски (?board=) в запросах и ключах доски и порций колонок;
 *   outcome и group не шлются никогда.
 */
import type { CrmRange } from '@/lib/crmTable';
import { boardActivityParams } from '@/lib/crmBoardActivity';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { toast } from 'sonner';
import { clientManagerChangedKeys, clientTemperatureChangedKeys, invalidateKeys } from '@/services/api/clientEditKeys';
import { clientsApi } from '@/services/api/clients';
import { crmApi } from '@/services/api/crm';
import { managersApi } from '@/services/api/managers';
import { ATTENTION_QUERY_KEY, CRM_PIPELINE_QUERY_KEY, DASHBOARD_FUNNEL_QUERY_KEY } from '@/services/api/queryKeys';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import { moveCardToColumn, type CrmCardEdit } from '@/lib/crmPipeline';
import type {
  CrmBoardView,
  CrmLeadFilterParams,
  CrmPipelineCard,
  CrmPipelineParams,
  CrmPipelineResponse,
} from '@/types/crmPipeline';

export const CRM_STAGE_PAGE_SIZE = 50;

const BOARD_KEY = [...CRM_PIPELINE_QUERY_KEY, 'board'] as const;

const NO_FILTERS: CrmLeadFilterParams = {};

export const crmPipelineKey = (
  board: CrmBoardView,
  activity: CrmRange | null,
  attentionOnly = false,
  filters: CrmLeadFilterParams = NO_FILTERS
) => [...BOARD_KEY, board, activity, attentionOnly, filters] as const;

function pipelineParams(
  board: CrmBoardView,
  activity: CrmRange | null,
  attentionOnly: boolean,
  filters: CrmLeadFilterParams = NO_FILTERS
): CrmPipelineParams {
  const params: CrmPipelineParams = { board, ...boardActivityParams(activity) };
  if (attentionOnly) params.attention = '1';
  return { ...params, ...filters };
}

export function useCrmPipeline(
  board: CrmBoardView,
  activity: CrmRange | null,
  attentionOnly = false,
  filters: CrmLeadFilterParams = NO_FILTERS
) {
  return useQuery({
    queryKey: crmPipelineKey(board, activity, attentionOnly, filters),
    queryFn: () => crmApi.getPipeline(pipelineParams(board, activity, attentionOnly, filters)),
    retry: false,
    // KAN-957: при смене фильтров и периода прежняя доска видна до ответа (панель фильтров
    // развёрнутой доски не размонтируется). Другой вид доски - другие колонки: не подставляем.
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[BOARD_KEY.length] === board ? previous : undefined,
  });
}

/** Префикс ключей всех порций одной колонки вида (любые фильтры). */
export const crmColumnPagesPrefix = (board: CrmBoardView, column: string) =>
  [...CRM_PIPELINE_QUERY_KEY, 'column', board, column] as const;

/** Ключ порций колонки ?board=&column=<key>. */
export const crmColumnCardsKey = (
  board: CrmBoardView,
  column: string | null,
  activity: CrmRange | null,
  attentionOnly = false,
  filters: CrmLeadFilterParams = NO_FILTERS
) => [...CRM_PIPELINE_QUERY_KEY, 'column', board, column, activity, attentionOnly, filters] as const;

/** Карточки колонки порциями по 50 (свёрнутая колонка, "Загрузить ещё"). Без колонки запроса нет. */
export function useCrmColumnCards(
  board: CrmBoardView,
  column: string | null,
  activity: CrmRange | null,
  attentionOnly = false,
  filters: CrmLeadFilterParams = NO_FILTERS
) {
  return useInfiniteQuery({
    queryKey: crmColumnCardsKey(board, column, activity, attentionOnly, filters),
    queryFn: ({ pageParam }) =>
      crmApi.getPipelineColumn({
        ...pipelineParams(board, activity, attentionOnly, filters),
        column: column as string,
        offset: pageParam,
        limit: CRM_STAGE_PAGE_SIZE,
      }),
    initialPageParam: 0,
    getNextPageParam: (last) => last.next_offset ?? undefined,
    enabled: column !== null,
    retry: false,
  });
}

type EditOf<K extends CrmCardEdit['type']> = Extract<CrmCardEdit, { type: K }>;

/**
 * Перенос с изменением клиента по типу: запрос, текст ошибки и кеши, которые
 * обновить после ответа (те же наборы, что у карточки клиента: clientEditKeys).
 */
const EDIT_KINDS: {
  [K in CrmCardEdit['type']]: {
    request: (clientId: string, edit: EditOf<K>) => Promise<unknown>;
    error: string;
    keys: (clientId: string) => QueryKey[];
  };
} = {
  manager: {
    request: (clientId, edit) => managersApi.assignToClient(clientId, edit.managerId),
    error: 'Не удалось назначить менеджера',
    keys: clientManagerChangedKeys,
  },
  temperature: {
    request: (clientId, edit) => clientsApi.update(clientId, { lead_temperature: edit.level }),
    error: 'Не удалось сменить теплоту',
    keys: clientTemperatureChangedKeys,
  },
  stage: {
    request: (clientId, edit) => crmApi.setClientStage(clientId, edit.stageId),
    error: 'Не удалось сменить этап',
    keys: () => [ATTENTION_QUERY_KEY, DASHBOARD_FUNNEL_QUERY_KEY],
  },
};

function editKind(edit: CrmCardEdit) {
  return EDIT_KINDS[edit.type] as {
    request: (clientId: string, edit: CrmCardEdit) => Promise<unknown>;
    error: string;
    keys: (clientId: string) => QueryKey[];
  };
}

export interface EditBoardCardVars {
  clientId: string;
  edit: CrmCardEdit;
  /** Поля карточки после переноса (оптимистично). */
  patch: Partial<CrmPipelineCard>;
  /** Колонка доски, куда перенесли (карточка сразу там); нет: колонка не меняется. */
  toColumn?: string | null;
  /** Колонка до переноса, если карточки нет в обзоре доски. */
  fromColumn?: string | null;
}

/** Перенос на доске boardKey (ключ crmPipelineKey текущего вида): кеш других видов только обновляется после ответа. */
export function useEditBoardCard(boardKey: readonly unknown[]) {
  const queryClient = useQueryClient();
  return useMutation<unknown, unknown, EditBoardCardVars, { snapshot: CrmPipelineResponse | undefined }>({
    mutationFn: ({ clientId, edit }) => editKind(edit).request(clientId, edit),
    onMutate: async ({ clientId, patch, toColumn, fromColumn }) => {
      await queryClient.cancelQueries({ queryKey: boardKey, exact: true });
      const snapshot = queryClient.getQueryData<CrmPipelineResponse>(boardKey);
      if (snapshot) {
        queryClient.setQueryData(boardKey, moveCardToColumn(snapshot, clientId, toColumn ?? null, fromColumn, patch));
      }
      return { snapshot };
    },
    onError: (error, { edit }, context) => {
      if (context?.snapshot) queryClient.setQueryData(boardKey, context.snapshot);
      toast.error(`${editKind(edit).error}: ${extractErrorMessage(error, 'ошибка сервера')}`);
    },
    onSettled: (_data, _error, { clientId, edit }) => invalidateKeys(queryClient, editKind(edit).keys(clientId)),
  });
}
